import { ID } from '@/types/model'
import { useProfile } from '@/hooks/useProfileId'
import Tabs from '@/components/Tabs'
import { homesCollection } from '@/reducers/homeResource'
import { Card } from '@/components'
import { behavioralEnrollmentCollection } from '@/reducers/deviceProgramEnrollments'
import DeviceProgramCards from '@/app/components/ProgramCards/DeviceProgramCards'
import { remoteConfig } from '@/firebase'
import UtilityProgramLearnMoreDashboardView from 'features/utility-programs/UtilityProgramLearnMoreDashboardView'
import useHideDashboardConfig from '@/authenticated/hooks/useHideDashboardConfig'
import { DashboardConfigType } from '@/types/dashboardConfig'
import UtilityProgramEnrollmentManager from 'features/utility-programs/UtilityProgramEnrollmentManager/UtilityProgramEnrollmentManager'

import useProgramDisplayType, {
  ProgramDisplay,
} from '@/hooks/useMultiProgramDisplay/useProgramDisplayType'
import useLegacyAvailablePrograms from '@/app/hooks/useLegacyAvailablePrograms'
import { useUrlSearchParam } from '@/hooks'
import { useHistory } from 'react-router-dom'
import HomeEstimation from '@/app/components/HomeEstimations/Prompt/HomeEstimation'
import HomeEstimatationCard from './components/HomeEstimatationCard'

type Props = {
  homeId: ID
}

export function useViewModel(props: Props) {
  const homeId = props.homeId

  const profile = useProfile()
  const { isLoadingOrIdle: isLoadingHome } = homesCollection.useFetch()
  const { data: behavioralEnrollments, isLoadingOrIdle: isLoadingEnrollments } =
    behavioralEnrollmentCollection.useFetch({
      require: !!profile?.id,
    })

  // TODO AVild - isAva nonsense
  const avaId = remoteConfig?.getWhitelabelId('ava')
  const { firstAvailableProgram, isLoading: isLoadingFirstAvailable } =
    useLegacyAvailablePrograms()
  const isAva = firstAvailableProgram && firstAvailableProgram.id === avaId

  const { programDisplayType } = useProgramDisplayType({ isAva })

  const isLoading =
    isLoadingHome || isLoadingEnrollments || isLoadingFirstAvailable

  const filteredEnrollment = behavioralEnrollments?.filter(
    (enrollment) => enrollment.profile_id === profile?.id,
  )?.[0]

  const offerHidden = useHideDashboardConfig(DashboardConfigType.UtilityProgram)

  return {
    isLoading,
    isAva,
    offerHidden,
    profile,
    filteredEnrollment,
    programDisplayType,
    homeId,
  }
}

function HomeDeviceSettingsTab(props: ReturnType<typeof useViewModel>) {
  return (
    <div className="flex flex-col w-full gap-4">
      <HomeEstimation variant="alert" />
      <HomeEstimatationCard
        redirectUrl={`devices/home/${props.homeId}?tab=settings`}
      />
    </div>
  )
}

function HomeDeviceProgramsTab(props: ReturnType<typeof useViewModel>) {
  const {
    isAva,
    offerHidden,
    filteredEnrollment,
    programDisplayType,
    profile,
  } = props

  // If we're isAva and the user hasn't enrolled behaviorally
  if (isAva && !filteredEnrollment && !offerHidden) {
    return <UtilityProgramLearnMoreDashboardView />
  }

  if (!profile) {
    return null
  }

  return (
    <div className="flex flex-col w-full gap-4">
      {programDisplayType === ProgramDisplay.MultiProgram && (
        <DeviceProgramCards showProgramApplications={false} />
      )}
      {programDisplayType === ProgramDisplay.Legacy && (
        <UtilityProgramEnrollmentManager
          deviceId={profile?.id}
          deviceType={'home'}
          wrapper={Card}
        />
      )}
    </div>
  )
}

export function HomeDeviceSection(props: Props) {
  const viewModel = useViewModel(props)

  const initialTab = useUrlSearchParam('tab')
  const tabs = ['Programs', 'Settings']
  const history = useHistory()
  const handleTabChange = (index: number) => {
    const tab = tabs[index]
    history.replace({ search: `?tab=${tab.toLowerCase()}` })
  }
  return (
    <div className="flex flex-col items-center space-y-6">
      <Tabs.Tabs
        initialTabIndex={+(initialTab === 'settings')}
        tabKey="device-home-tabs"
        labels={tabs}
        tabBarAttributes={{ className: 'max-w-none' }}
        onTabChange={handleTabChange}
      />
      <Tabs.Content tabKey="device-home-tabs">
        <HomeDeviceProgramsTab {...viewModel} />
        <HomeDeviceSettingsTab {...viewModel} />
      </Tabs.Content>
    </div>
  )
}
